import { IAsset, IThematicData, IThematicEventLevel, LevelType } from "interfaces";

// On submit the backend only cares about the asset_id; we strip everything
// else (key, url, content_type) to keep the payload minimal and unambiguous.
// `null` signals "no image" (the v2 contract — empty string is no longer
// accepted).
const serializeAssetField = (a: IAsset | null | undefined): { asset_id: string } | null =>
  a && a.asset_id ? { asset_id: a.asset_id } : null;

/**
 * Build the final `thematic` object from raw form values produced by ThematicBlock.
 *
 * Contract (per docs/03-implementation-plan.md):
 *  - `thematic: null` (or absent) tells the backend the event is not thematic;
 *    when present, the levels array fully replaces what's stored.
 *
 * Behaviors:
 *  - Returns `null` when the top-level `is_thematic` flag is false/absent.
 *  - Renumbers levels 1..N from final array order (level_number is never a form
 *    field — the user reorders cards, we derive the number on submit).
 *  - For trivia levels: emits ONLY the fields matching the current mode.
 *    The TriviaLevelConfig component already clears the hidden side on toggle;
 *    we defensively pick here too in case stale values slipped through.
 *  - For minigame: parses the `config_raw` JSON textarea into an object and
 *    drops the helper field. The form validator already rejected invalid JSON.
 */
export function serializeThematicFromForm(values: any): IThematicData | null {
  if (!values?.is_thematic) return null;

  const src = values.thematic ?? {};
  const levels: IThematicEventLevel[] = (src.levels ?? []).map(
    (lvl: any, idx: number): IThematicEventLevel => {
      const levelType = lvl.level_type as LevelType;
      let config: any = {};

      if (levelType === "trivia") {
        const c = lvl.config ?? {};
        if (c.trivia_id) {
          // Unique mode: pin a specific quiz, omit level/keywords.
          config = { trivia_id: c.trivia_id };
        } else {
          // Pool mode: omit trivia_id; level/keywords drive selection.
          config = {
            level: c.level,
            keywords: Array.isArray(c.keywords) ? c.keywords : [],
          };
        }
      } else if (levelType === "jigsort") {
        const c = lvl.config ?? {};
        config = {
          title: c.title ?? {},
          description: c.description ?? {},
          difficulty: c.difficulty,
          // Optional Asset — same shape/lifecycle as thematic_event image
          // fields. Emits `{asset_id}` or null.
          image: serializeAssetField(c.image),
        };
      } else if (levelType === "minigame") {
        // Validator already enforced JSON validity; fall back to {} on parse
        // failure so we never serialize a non-object.
        try {
          config = lvl.config_raw ? JSON.parse(lvl.config_raw) : (lvl.config ?? {});
        } catch {
          config = {};
        }
      }

      return {
        level_number: idx + 1,
        level_type: levelType,
        config,
        // thematic_event_level_id intentionally omitted on write (backend replaces all)
      };
    }
  );

  return {
    title: src.title ?? {},
    description: src.description ?? {},
    character_key: src.character_key,
    // 0 = lives mechanic disabled (per backend plan). Default to 0 when the
    // form never had a value — matches the default-off state of the
    // "Lives enabled" switch in ThematicBlock.
    initial_lives: typeof src.initial_lives === "number" ? src.initial_lives : 0,
    card_image: serializeAssetField(src.card_image),
    banner_image: serializeAssetField(src.banner_image),
    background_image: serializeAssetField(src.background_image),
    cta_link: src.cta_link,
    cta_icon_key: src.cta_icon_key,
    cta_title: src.cta_title ?? {},
    cta_description: src.cta_description ?? {},
    cta_button_text: src.cta_button_text ?? {},
    levels,
  };
}

/**
 * Convert a `record.thematic` from the backend into the shape antd Form
 * expects as initial values under the keys { is_thematic, thematic }.
 *
 * Notably:
 *  - For minigame levels, stringify config into config_raw for the textarea
 *    and drop config so antd doesn't see two representations of the same field.
 *  - Trivia levels are kept as-is; TriviaLevelConfig reads config.trivia_id at
 *    mount to infer initial unique-mode state.
 */
export function hydrateThematicForForm(
  thematic: IThematicData | null | undefined
): { is_thematic: boolean; thematic?: any } {
  if (!thematic) return { is_thematic: false };
  return {
    is_thematic: true,
    thematic: {
      ...thematic,
      levels: (thematic.levels ?? []).map((lvl) => {
        if (lvl.level_type === "minigame") {
          return {
            ...lvl,
            config_raw: JSON.stringify(lvl.config ?? {}, null, 2),
            config: undefined,
          };
        }
        return lvl;
      }),
    },
  };
}
